import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import kebabCase from 'lodash/kebabCase'

import { PLAN_FEATURES_CONFIGS } from '@/constants/plan'
import { Company } from '@/lib/types/CompanyT'
import { PlanFeatureType, PlanFeatureTypeArray } from '@/lib/types/PlanT'
import { SubscriptionType } from '@/lib/types/SubscriptionT'
import { getActiveFeatures, getSubscriptionInfo } from '@/api/company/subscription'

export interface CompanyState {
  data: (Company & { slug: string }) | null
  features: PlanFeatureType | null
  loading: boolean
  error: string | null
}

const initialState: CompanyState = {
  data: null,
  features: null,
  loading: false,
  error: null,
}

/* -------------------------------------------------------
    ASYNC THUNK (Cleaner)
------------------------------------------------------- */
export const fetchAndUpdateFeatureUsage = createAsyncThunk<
  PlanFeatureTypeArray,
  void,
  { rejectValue: string }
>('company/fetchAndUpdateFeatureUsage', async (_, { rejectWithValue }) => {
  try {
    return await getActiveFeatures()
  } catch (err) {
    return rejectWithValue('Failed to load feature usage')
  }
})

export const fetchSubscriptionInfo = createAsyncThunk<
  SubscriptionType,
  void,
  { rejectValue: string }
>('company/fetchSubscriptionInfo', async (_, { rejectWithValue }) => {
  try {
    return await getSubscriptionInfo()
  } catch (err) {
    return rejectWithValue('Failed to load subscription info')
  }
})

/* -------------------------------------------------------
    SLICE
------------------------------------------------------- */
const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    setCompany(state, action: PayloadAction<Company>) {
      state.data = {
        ...action.payload,
        slug: kebabCase(action.payload.name),
      }

      state.error = null
    },

    clearCompany(state) {
      state.data = null
      state.features = null
      state.error = null
      state.loading = false
    },

    setCompanyFeatures(state, action: PayloadAction<PlanFeatureType>) {
      state.features = action.payload
      state.error = null
    },

    updateCurrentPriceId(state, action: PayloadAction<string | null>) {
      if (state.data) {
        state.data.currentPriceId = action.payload
      }
    },
  },

  /* -------------------------------------------------------
      EXTRA REDUCERS — Async Workflow
  ------------------------------------------------------- */
  extraReducers: (builder) => {
    builder
      .addCase(fetchAndUpdateFeatureUsage.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAndUpdateFeatureUsage.fulfilled, (state, action) => {
        state.loading = false

        state.features = action.payload.reduce<PlanFeatureType>(
          (acc, feature) => ({
            ...acc,
            [feature.type]: PLAN_FEATURES_CONFIGS[feature.type].hasLimit
              ? feature
              : { isAvailable: feature?.isAvailable },
          }),
          {} as PlanFeatureType
        )
      })
      .addCase(fetchAndUpdateFeatureUsage.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Unexpected error'
      })
      // Subscription Info Cases
      .addCase(fetchSubscriptionInfo.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchSubscriptionInfo.fulfilled, (state, action) => {
        state.loading = false

        if (state.data) {
          state.data.subscription = {
            id: action.payload.id,
            status: action.payload.status,
            price: {
              id: action.payload.id,
              planId: action.payload.planId,
              amount: action.payload.price.amount,
              interval: action.payload.price.type,
              name: action.payload.planName,
            },
          }
        }
      })
      .addCase(fetchSubscriptionInfo.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to load subscription info'
      })
  },
})

export const { setCompany, clearCompany, setCompanyFeatures, updateCurrentPriceId } =
  companySlice.actions

export default companySlice.reducer
